import partner from "../assets/images/order/delivary-partner.png";
import message from "../assets/images/order/message.png";
import call from "../assets/images/order/call.png";
import chat from "../assets/images/order/bubble-chat.png";
import { OrderDetail, OrderDetailResponse } from "../utils/types";
import { useEffect, useState } from "react";
import { restaurantOrderService } from "../services";

const NewOrder = ({
  id,
  tab,
  selectedOrderIndex,
}: {
  id?: string;
  tab:
    | "new"
    | "confirmed"
    | "preparing"
    | "ready_for_pickup"
    | "out_for_delivery"
    | "delivered"
    | "cancelled";
  selectedOrderIndex?: number;
}) => {
  const rating = 5;
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch single order details from API
  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!id) {
        setOrderDetail(null);
        return;
      }

      setLoading(true);
      try {
        const response = await restaurantOrderService.getOrderById(id);

        if (response?.status && response?.data) {
          setOrderDetail(response.data);
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [id]);

  // If no order is selected, show a message
  if (!id || selectedOrderIndex === undefined || selectedOrderIndex === -1) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500 dark:text-gray-400 transition-colors">Select an order to view details</p>
      </div>
    );
  }

  // If loading, show loading message
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500 dark:text-gray-400 transition-colors">Loading order details...</p>
      </div>
    );
  }

  // If no order detail, show error message
  if (!orderDetail) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500 dark:text-gray-400 transition-colors">Unable to load order details</p>
      </div>
    );
  }

  // Format time from ISO string to readable format
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="px-2 md:px-6 py-8 pb-20 md:pb-0 dark:bg-gray-700 ">
      <div className="mb-8">
        <div className="border-2 dark:border-gray-700 p-[12px] rounded-[16px] bg-white dark:bg-gray-800 transition-colors">
          {/* Customer Info */}
          <div className="flex justify-between items-center border-b-2 dark:border-gray-700 py-[3px] transition-colors">
            <div className="flex gap-[8px] items-center">
              <div className="flex items-center gap-4">
                <div className="h-[75px] w-[75px] rounded-[12px] bg-gray-200 flex items-center justify-center overflow-hidden">
                  {orderDetail.user?.image ? (
                    <img
                      src={orderDetail.user.image}
                      alt={`${orderDetail.user.firstName} ${orderDetail.user.lastName}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">👤</span>
                  )}
                </div>
              </div>
              <div className="">
                <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-medium leading-[20px] transition-colors">
                  {orderDetail.user?.firstName} {orderDetail.user?.lastName}
                </p>
                <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[10px] font-normal leading-[17px] transition-colors">
                  {orderDetail.deliveryAddress.city}
                  <span className="ml-2">
                    {formatTime(orderDetail.createdAt)}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              {/* <div className="flex justify-center items-center h-[36px] w-[36px] rounded-full border-2">
                <img src={message} alt="message" className="h-full w-full" />
              </div> */}
              {orderDetail.deliveryAddress?.phone && (
                <div>
                  <a
                    href={`tel:${orderDetail.deliveryAddress.phone}`}
                    className="relative group flex justify-center items-center md:hidden h-[36px] w-[36px] rounded-full border-2 dark:border-gray-600 cursor-pointer transition-colors"
                  >
                    <img src={call} alt="call" className="h-[20px] w-[20px]" />
                  </a>

                  <div className="relative group hidden md:flex justify-center items-center h-[36px] w-[36px] rounded-full border-2 dark:border-gray-600 cursor-pointer hover:border-green-500 dark:hover:border-green-500 transition-colors">
                    <img src={call} alt="call" className="h-full w-full" />

                    <span className="absolute bottom-full mb-2 px-3 py-1 bg-gray-800 dark:bg-gray-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      {orderDetail.deliveryAddress.phone}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Items List */}
          <div>
            {orderDetail.items.map((item) => (
              <div
                key={item._id}
                className="flex justify-between items-center py-[8px]"
              >
                <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[12px] font-medium leading-[20px] transition-colors">
                  {item.quantity} x {item.dishName}
                  {item.variantName && ` (${item.variantName})`}
                </p>
                <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[12px] font-medium leading-[20px] transition-colors">
                  ₹{item.totalItemPrice}.00
                </p>
              </div>
            ))}
          </div>

          {/* Subtotal, Tax, Delivery Fee */}
          <div className="border-t dark:border-gray-700 pt-2 transition-colors">
            <div className="flex justify-between items-center py-[4px]">
              <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[20px] transition-colors">
                Subtotal
              </p>
              <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[20px] transition-colors">
                ₹{orderDetail.subtotal}.00
              </p>
            </div>
            <div className="flex justify-between items-center py-[4px]">
              <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[20px] transition-colors">
                Tax
              </p>
              <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[20px] transition-colors">
                ₹{orderDetail.taxAmount}.00
              </p>
            </div>
            <div className="flex justify-between items-center py-[4px]">
              <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[20px] transition-colors">
                Delivery Fee
              </p>
              <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[20px] transition-colors">
                ₹{orderDetail.deliveryFee}.00
              </p>
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-between items-center py-[12px] border-t-2 border-dashed dark:border-gray-700 mt-2 transition-colors">
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-medium leading-[160%] transition-colors">
              Total
            </p>
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-medium leading-[160%] transition-colors">
              ₹{orderDetail.totalAmount}.00
            </p>
          </div>

          {/* status */}
          <div className="flex justify-between items-center py-[12px]">
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[12px] font-medium leading-[20px] transition-colors">
              Status
            </p>
            {orderDetail.orderStatus === "placed" ? (
              <span className="inline-flex items-center rounded-md bg-blue-400/10 px-2 py-1 text-xs font-medium text-blue-500 inset-ring inset-ring-blue-400/20">
                Placed
              </span>
            ) : orderDetail.orderStatus === "confirmed" ||
              orderDetail.orderStatus === "preparing" ||
              orderDetail.orderStatus === "ready_for_pickup" ? (
              <span className="inline-flex items-center rounded-md bg-yellow-400/10 px-2 py-1 text-xs font-medium text-yellow-500 inset-ring inset-ring-yellow-400/20">
                {orderDetail.orderStatus.replace("_", " ").toUpperCase()}
              </span>
            ) : orderDetail.orderStatus === "out_for_delivery" ? (
              <span className="inline-flex items-center rounded-md bg-orange-400/10 px-2 py-1 text-xs font-medium text-orange-500 inset-ring inset-ring-orange-400/20">
                OUT FOR DELIVERY
              </span>
            ) : orderDetail.orderStatus === "delivered" ? (
              <span className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-500 inset-ring inset-ring-green-400/20">
                DELIVERED
              </span>
            ) : orderDetail.orderStatus === "cancelled" ? (
              <span className="inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-500 inset-ring inset-ring-red-400/20">
                Cancelled
              </span>
            ) : null}
          </div>

          {/* payment method */}
          <div className="flex justify-between items-center pb-[12px]">
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[12px] font-medium leading-[20px] transition-colors">
              Payment Method
            </p>
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[12px] font-medium leading-[20px] transition-colors">
              {orderDetail.paymentMethod === "cod"
                ? "Cash on Delivery"
                : orderDetail.paymentMethod.toUpperCase()}
            </p>
          </div>
        </div>
      </div>

      {/* cooking instructions  delivery details */}
      <div>
        <div
          className={`${orderDetail?.cookingInstructions ? "block" : "hidden"} border-2 dark:border-gray-700 p-[12px] rounded-[16px] mb-8 bg-white dark:bg-gray-800 transition-colors`}
        >
          <div className="pb-[12px] border-b dark:border-gray-700 transition-colors">
            <h3 className="text-[#050505] dark:text-gray-200 font-[Urbanist] text-[16px] font-semibold leading-[140%] transition-colors">
              Cooking Instructions
            </h3>
          </div>
          <div className="flex justify-between items-center gap-2 mt-[12px]">
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-medium leading-[160%] transition-colors">
              Please don’t add garlic and add extra ketchups
            </p>
            <img src={chat} alt="chat" className="h-[24px] w-[24px]" />
          </div>
        </div>

        <div
          className={`${orderDetail.deliveryAgent ? "block" : "hidden"} border-2 dark:border-gray-700 p-[12px] rounded-[16px] bg-white dark:bg-gray-800 transition-colors`}
        >
          <div className="border-b-2 dark:border-gray-700 pb-[12px] transition-colors">
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-semibold leading-[140%] transition-colors">
              Delivery Details{" "}
            </p>
          </div>
          <div className="flex justify-between items-center border-b-2 dark:border-gray-700  border-dashed py-[12px] transition-colors">
            <div className="flex gap-[8px] items-center">
              <div className="flex items-center gap-4">
                <div className="h-[75px] w-[75px] rounded-[12px] bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden transition-colors">
                  {orderDetail.deliveryAgent?.image ? (
                    <img
                      src={orderDetail.deliveryAgent.image}
                      alt={orderDetail.deliveryAgent.fullName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <img
                      src={partner}
                      alt="partner"
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
              </div>
              <div className="">
                <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-medium leading-[20px] transition-colors">
                  {orderDetail.deliveryAgent?.fullName || "Not Assigned"}
                </p>
                <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[10px] font-normal leading-[17px] transition-colors">
                  {orderDetail.deliveryAgent
                    ? "Delivery Partner"
                    : "Waiting for assignment"}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              {/* <div className="flex justify-center items-center h-[36px] w-[36px] rounded-full border-2">
                <img
                  src={message}
                  alt="message"
                  className="h-[20px] w-[20px]"
                />
              </div> */}
              {orderDetail.deliveryAgent?.phoneNumber && (
                <a
                  href={`tel:${orderDetail.deliveryAgent.phoneNumber}`}
                  className="relative group flex justify-center items-center h-[36px] w-[36px] rounded-full border-2 dark:border-gray-600 cursor-pointer hover:border-green-500 dark:hover:border-green-500 transition-colors"
                >
                  <img src={call} alt="call" className="h-[20px] w-[20px]" />
                  {/* Desktop tooltip */}
                  <span className="hidden md:block absolute bottom-full mb-2 px-3 py-1 bg-gray-800 dark:bg-gray-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {orderDetail.deliveryAgent.phoneNumber}
                  </span>
                </a>
              )}
            </div>
          </div>

          {/* address section  */}
          <div className="py-[12px]">
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-medium leading-[160%] transition-colors">
              {orderDetail.deliveryAddress.addressLine1}
              {orderDetail.deliveryAddress.addressLine2 &&
                `, ${orderDetail.deliveryAddress.addressLine2}`}
              , {orderDetail.deliveryAddress.city},{" "}
              {orderDetail.deliveryAddress.pincode}
            </p>
            <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[160%] mt-1 transition-colors">
              Contact: {orderDetail.deliveryAddress.phone}
            </p>
          </div>

          {/* <div className="flex justify-between items-center pt-[12px]">
            <span className="text-[#050505] font-Urbanist text-[10px] font-normal leading-[17px]">
              Arriving Soon!
            </span>
            <span className="text-[#02A72B] font-Urbanist text-[10px] font-normal leading-[17px]">
              ON THE WAY
            </span>
          </div> */}
        </div>
      </div>

      <div className="hidden border-2 dark:border-gray-700 p-[12px] rounded-[16px] my-[16px] bg-white dark:bg-gray-800 transition-colors">
        <div className="pb-[12px] border-b dark:border-gray-700 transition-colors">
          <h3 className="text-[#050505] dark:text-gray-200 font-[Urbanist] text-[16px] font-semibold leading-[140%] transition-colors">
            Need Help?
          </h3>
        </div>
        <div className="flex justify-between items-center gap-2 mt-[12px]">
          <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-medium leading-[160%] transition-colors">
            Chat with our customer support
          </p>
          <img src={chat} alt="chat" className="h-[24px] w-[24px]" />
        </div>
      </div>
    </div>
  );
};

export default NewOrder;
